import {DatasetColumnDataType, DatasetColumnRole, DatasetType, TimeGranularity, type Dataset} from "@vesta/vos-types/modules/workspaceLevel/domain/Dataset";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {robustJsonResultClientFetch} from "@vesta/error-handling-remix/fetches/robustJsonResultClientFetch";
import {dataResult, errorResult, type Result} from "@vesta/error-handling-remix/result";
import {z as zod} from "zod";
import {uuidZodValidator} from "@vesta/global-common-typescript/validators/zodValidators";
import {AnalyticsQueryVariant, type DataResolverProps, type PeriodicAsPeriodicDataResolverProps, type RawData} from "@vesta/vos-types/modules/workspaceLevel/domain/RawDataType";
import {type AnyResolvedDashboardDatasetsAndColumns, transformDataIfNeeded} from "~/routes/_withAuthenticationStatus/_authenticated/_withProfileStatus/_profileCompleted/w/$workspaceId/ottopilot/_ottopilotScaffold/dashboard/-helpers";
import type {QueryClient} from "@tanstack/react-query";
import type {OrganizationalUnitTemplate} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/organizationalUnitsWorkspaceCapability/domain/OrganizationalUnitTemplate";
import type {User} from "@vesta/vos-types/modules/workspaceLevel/users/domain/User";
import type {UserTemplate} from "@vesta/vos-types/modules/workspaceLevel/users/domain/UserTemplate";

const datasetValidator = zod.object({
    id: uuidZodValidator,
    displayName: zod.string(),
    type: zod.enum(DatasetType),
    granularity: zod.enum(TimeGranularity).nullable(),
    columns: zod.array(zod.object({
        id: uuidZodValidator,
        displayName: zod.string(),
        role: zod.enum(DatasetColumnRole),
        dataType: zod.enum(DatasetColumnDataType),
        resolutionDetails: zod.unknown(),
    })),
});

const rawDataValidator = zod.object({
    columnMetadata: zod.array(zod.object({
        id: uuidZodValidator,
        displayName: zod.string(),
        columnIndex: zod.number(),
        dataType: zod.enum(DatasetColumnDataType),
        role: zod.enum(DatasetColumnRole),
    })),
    dataPoints: zod.array(zod.array(zod.object({
        value: zod.union([zod.string(), zod.number(), zod.null()]),
    }))),
});

type RawDatasetFromApi = {
    id: Uuid;
    displayName: string;
    type: DatasetType;
    granularity: TimeGranularity | null;
    columns: Array<{
        id: Uuid;
        displayName: string;
        role: DatasetColumnRole;
        dataType: DatasetColumnDataType;
        resolutionDetails: unknown;
    }>;
};

function makeRawDatasetTypeSafe(rawDataset: RawDatasetFromApi): Dataset {
    const base = {
        id: rawDataset.id,
        displayName: rawDataset.displayName,
        columns: rawDataset.columns,
    } as const;

    switch (rawDataset.type) {
        case DatasetType.periodic: {
            const granularity: TimeGranularity = rawDataset.granularity ?? TimeGranularity.daily;
            if (rawDataset.granularity === null) {
                console.error("Granularity is required for periodic datasets; defaulting to daily.");
            }
            const periodic: Dataset = {
                ...base,
                type: DatasetType.periodic,
                granularity,
            };
            return periodic;
        }
        case DatasetType.pointInTime: {
            const pointInTime: Dataset = {
                ...base,
                type: DatasetType.pointInTime,
            };
            return pointInTime;
        }
        case DatasetType.nonTimeSeries: {
            const nonTimeSeries: Dataset = {
                ...base,
                type: DatasetType.nonTimeSeries,
            };
            return nonTimeSeries;
        }
    }
}

export async function getDataset(datasetId: Uuid, intellsysBaseUrl: string, accessToken: string, workspaceId: Uuid): Promise<Result<Dataset>> {
    const result = await robustJsonResultClientFetch(
        `${intellsysBaseUrl}/sub-system/pluto/ottopilot/analytics/datasets/get`,
        {
            method: "POST",
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                workspaceId: workspaceId,
                id: datasetId,
            }),
        },
        zod.array(datasetValidator),
    );
    if (result.success !== true) {
        return result;
    }
    const datasets = result.data;
    if (datasets.length !== 1) {
        return errorResult(new Error("Expected 1 dataset, got " + datasets.length));
    }
    const dataset = datasets[0]!;
    return dataResult(makeRawDatasetTypeSafe(dataset));
}

export async function getDatasets(intellsysBaseUrl: string, accessToken: string, workspaceId: Uuid): Promise<Result<Array<Dataset>>> {
    const result = await robustJsonResultClientFetch(
        `${intellsysBaseUrl}/sub-system/pluto/ottopilot/analytics/datasets/get`,
        {
            method: "POST",
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                workspaceId: workspaceId,
            }),
        },
        zod.array(datasetValidator),
    );
    if (result.success !== true) {
        return result;
    }
    return dataResult(result.data.map(makeRawDatasetTypeSafe));
}

export async function fetchAnalyticsData(
    intellsysBaseUrl: string,
    accessToken: string,
    workspaceId: Uuid,
    queryClient: QueryClient,
    users: Array<User>,
    userTemplates: Array<UserTemplate>,
    ouTemplates: Array<OrganizationalUnitTemplate>,
    resolvedDashboard: AnyResolvedDashboardDatasetsAndColumns,
    dataset: Dataset,
    props: DataResolverProps,
    amaronEventContainerDispositions?: Array<{id: Uuid; displayName: string}>,
): Promise<Result<RawData>> {
    const {
        datasetType,
        queryVariant,
        ...rest
    } = props;

    let endpoint: string;
    let payload: Record<string, unknown>;

    if (queryVariant === AnalyticsQueryVariant.periodicAsPeriodic) {
        endpoint = "/ottopilot/analytics/query/periodic-as-periodic";
        payload = {
            workspaceId,
            ...rest,
        };
    } else if (queryVariant === AnalyticsQueryVariant.periodicAsNonTimeSeries) {
        endpoint = "/ottopilot/analytics/query/periodic-as-non-time-series";
        const {granularity: _g, timeZone: _tz, ...periodicNonTsRest} = rest as typeof rest & {granularity?: unknown; timeZone?: unknown};
        payload = {
            workspaceId,
            ...periodicNonTsRest,
        };
    } else {
        endpoint = "/ottopilot/analytics/query/point-in-time-as-non-time-series";
        const {granularity: _g, timeZone: _tz, ...pitRest} = rest as typeof rest & {granularity?: unknown; timeZone?: unknown};
        payload = {
            workspaceId,
            ...pitRest,
        };
    }

    const result = await robustJsonResultClientFetch(
        `${intellsysBaseUrl}/sub-system/pluto${endpoint}`,
        {
            method: "POST",
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify(payload),
        },
        rawDataValidator,
    );
    if (result.success !== true) {
        return result;
    }
    return transformDataIfNeeded({
        workspaceId: workspaceId,
        intellsysBaseUrl: intellsysBaseUrl,
        accessToken: accessToken,
        queryClient: queryClient,
        users: users,
        userTemplates: userTemplates,
        ouTemplates: ouTemplates,
        resolvedDashboard: resolvedDashboard,
        dataset: dataset,
        props: props,
        data: result.data,
        amaronEventContainerDispositions: amaronEventContainerDispositions,
    });
}

export async function fetchPeriodicDataFromPeriodicDataset(
    intellsysBaseUrl: string,
    accessToken: string,
    workspaceId: Uuid,
    queryClient: QueryClient,
    users: Array<User>,
    userTemplates: Array<UserTemplate>,
    ouTemplates: Array<OrganizationalUnitTemplate>,
    resolvedDashboard: AnyResolvedDashboardDatasetsAndColumns,
    dataset: Dataset,
    props: PeriodicAsPeriodicDataResolverProps
): Promise<Result<RawData>> {
    return fetchAnalyticsData(
        intellsysBaseUrl,
        accessToken,
        workspaceId,
        queryClient,
        users,
        userTemplates,
        ouTemplates,
        resolvedDashboard,
        dataset,
        props,
    );
}
